import { useState } from 'react';
import { timeZoneLabel } from '@ruby-cms/shared';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, InlineNotification, Modal, NumberInput, Pagination, Select, SelectItem, Tag, TextArea, TextInput, Toggle,
} from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Add, TrashCan } from '@carbon/icons-react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

// VIP and loyalty (P1-D07): the reward list an admin pays (no second admin since 2026-09-29, owner),
// the program settings, and the VIP panel of a player (UserDetail).

type Level = { cap: number; ten: string; diem: number; thuongLenCap: number; hoanTraBps: number; hoanTraToiDa: number };
type Program = {
  bat: boolean; batLuc: string | null; vinMoiDiem: number; capChatVip: number; chuKyHoanTra: 'ngay' | 'tuan';
  hoanTraToiThieu: number; boQuaGame: string[]; cap: Level[];
  khoangBat: { tu: string; den: string | null }[]; choDuyetGio: number;
};
type Config = { program: Program; updatedAt: string | null; updatedBy: string | null };
type Reward = {
  id: string; gameUserId: number; nick: string; kind: 'LEVEL_UP' | 'REBATE'; ref: string; refLabel: string; level: number; levelName: string;
  basis: string; rateBps: number; amount: string; status: string; submittedByName: string | null; approvedByName: string | null;
  decisionNote: string | null; commandKey: string | null; createdAt: string; paidAt: string | null; approvableAt: string | null;
};
type RewardPage = { items: Reward[]; total: number; sum: string; page: number; pageSize: number };
type Summary = { enabled: boolean; levels: { level: number; name: string; players: number }[]; rewards: { status: string; kind: string; count: number; amount: string }[] };

const STATUS: Record<string, [string, 'purple' | 'blue' | 'teal' | 'green' | 'red' | 'gray']> = {
  PROPOSED: ['Chờ chi', 'purple'],
  SUBMITTED: ['Chờ duyệt (cũ)', 'blue'],
  APPROVED: ['Đang chi', 'teal'],
  PAID: ['Đã chi', 'green'],
  FAILED: ['Hệ A từ chối', 'red'],
  REJECTED: ['Từ chối', 'gray'],
};
const KIND: Record<string, string> = { LEVEL_UP: 'Thưởng lên cấp', REBATE: 'Hoàn trả' };
const RewardStatus = ({ s }: { s: string }) => { const [l, t] = STATUS[s] ?? [s, 'gray']; return <Tag size="sm" type={t}>{l}</Tag>; };

const FIELDS: FilterField[] = [
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ...Object.entries(STATUS).map(([k, v]) => [k, v[0]] as [string, string])] },
  { key: 'kind', label: 'Loại', kind: 'select', options: [['', 'Tất cả'], ['LEVEL_UP', 'Thưởng lên cấp'], ['REBATE', 'Hoàn trả']] },
  { key: 'userId', label: 'Mã người chơi', kind: 'number' },
  { key: 'ref', label: 'Kỳ hoặc cấp', kind: 'text', placeholder: 'W2026-09-21, D2026-09-27 hoặc 3' },
];
const KEYS = FIELDS.map((f) => f.key);

const columns: ColumnDef<Reward, any>[] = [
  { header: 'Người chơi', id: 'u', cell: (c) => <Link className="cell-link" to={'/users/' + c.row.original.gameUserId} onClick={(e) => e.stopPropagation()}>{c.row.original.nick || '#' + c.row.original.gameUserId}</Link> },
  { header: 'Loại', accessorKey: 'kind', cell: (c) => KIND[c.getValue()] ?? c.getValue() },
  { header: 'Kỳ / cấp', accessorKey: 'refLabel' },
  { header: 'Cấp VIP', accessorKey: 'levelName' },
  { header: 'Tiền cược tính', accessorKey: 'basis', cell: (c) => c.row.original.kind === 'REBATE' ? <Money v={c.getValue()} /> : <span className="muted">-</span> },
  { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} unit /> },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <RewardStatus s={c.getValue()} /> },
  { header: 'Tạo lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];

export function VipRewardsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const f = useUrlFilters(KEYS);
  const q = useQuery({
    queryKey: ['vip-rewards', f.query, f.page, f.pageSize],
    queryFn: () => api.get<RewardPage>('/api/admin/vip/rewards', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const sum = useQuery({ queryKey: ['vip-summary'], queryFn: () => api.get<Summary>('/api/admin/vip/summary') });
  // pay: PROPOSED rewards paid at once; approve: rewards left SUBMITTED from before approval was removed
  const [act, setAct] = useState<{ kind: 'pay' | 'approve' | 'reject'; rows: Reward[] } | null>(null);
  const [note, setNote] = useState('');
  const [ack, setAck] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/api/admin/vip/rewards/' + act!.kind, {
      ids: act!.rows.map((r) => r.id), ...(note.trim() ? { note: note.trim() } : {}),
      ...(act!.kind !== 'reject' && ack.trim() ? { refundChecked: ack.trim() } : {}),
    }),
    onSuccess: (r) => {
      const k = act!.kind;
      setDone(k !== 'reject'
        ? `Đã chi ${r.approved} thưởng${r.tooEarly ? `, ${r.tooEarly} thưởng chưa hết thời gian chờ` : ''}${r.belowLevel ? `, ${r.belowLevel} thưởng lên cấp mà người chơi không còn đủ điểm (hãy từ chối)` : ''}${r.refunded ? `, ${r.refunded} thưởng bị giữ vì người chơi có hoàn cược có thể chạm tới tiền cược tạo ra nó: kiểm tra rồi từ chối, hoặc chi lại kèm nội dung đã kiểm tra` : ''}.`
        : `Đã từ chối ${r.rejected} thưởng.`);
      setAct(null); setNote(''); setAck('');
      qc.invalidateQueries({ queryKey: ['vip-rewards'] }); qc.invalidateQueries({ queryKey: ['vip-summary'] });
    },
  });
  const items = q.data?.items ?? [];
  const proposed = items.filter((r) => r.status === 'PROPOSED');
  const submitted = items.filter((r) => r.status === 'SUBMITTED');
  const total = (rows: Reward[]) => rows.reduce((a, r) => a + BigInt(r.amount), 0n);
  const waiting = (s: string) => (sum.data?.rewards ?? []).filter((r) => r.status === s).reduce((a, r) => ({ n: a.n + r.count, v: a.v + BigInt(r.amount) }), { n: 0, v: 0n });
  const [pick, setPick] = useState<Reward | null>(null);

  return (
    <div className="page">
      <PageHeader title="Thưởng VIP"
        lead="Thưởng lên cấp và hoàn trả theo kỳ do hệ thống tính từ tiền cược đã chốt. Bấm chi là cộng tiền ngay, không chờ người duyệt; mỗi thưởng được cộng đúng một lần kèm vòng cược và thư báo cho người chơi."
        actions={can('vip.config') || can('vip.view') ? <Link className="cds--btn cds--btn--tertiary" to="/vip/config">Cấu hình VIP</Link> : null} />
      {sum.data ? (
        <div className="kpis">
          <div className="kpi"><span>Chương trình</span><strong>{sum.data.enabled ? 'Đang bật' : 'Đang tắt'}</strong></div>
          <div className="kpi"><span>Chờ chi</span><strong>{waiting('PROPOSED').n} · <Money v={waiting('PROPOSED').v} /></strong></div>
          {waiting('SUBMITTED').n ? <div className="kpi"><span>Chờ duyệt (cũ)</span><strong>{waiting('SUBMITTED').n} · <Money v={waiting('SUBMITTED').v} /></strong></div> : null}
          <div className="kpi"><span>Đã chi</span><strong>{waiting('PAID').n} · <Money v={waiting('PAID').v} /></strong></div>
        </div>
      ) : null}
      {done ? <InlineNotification kind="success" lowContrast title={done} onClose={() => setDone(null)} /> : null}
      <div className="section"><FilterBar id="vip" fields={FIELDS} filters={f} exportPath="/api/admin/vip/rewards" /></div>
      <div className="row section">
        {can('vip.payout.submit') ? <Button size="sm" disabled={!proposed.length} onClick={() => { run.reset(); setAct({ kind: 'pay', rows: proposed }); }}>Chi {proposed.length} thưởng trên trang</Button> : null}
        {can('vip.payout.approve') && submitted.length ? <Button size="sm" kind="tertiary" onClick={() => { run.reset(); setAct({ kind: 'approve', rows: submitted }); }}>Duyệt {submitted.length} thưởng chờ duyệt từ trước</Button> : null}
        {q.data ? <span className="muted">Tổng theo bộ lọc: <Money v={q.data.sum} unit /></span> : null}
      </div>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(r) => setPick(r)} rowKey={(r) => r.id}
        empty={{ title: 'Không có thưởng nào', body: sum.data && !sum.data.enabled ? 'Chương trình VIP đang tắt. Bật và đặt tỷ lệ ở trang Cấu hình VIP.' : 'Thưởng lên cấp xuất hiện khi người chơi đạt cấp; hoàn trả xuất hiện khi kỳ kết thúc.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, t) => `${min} đến ${max} trong ${t.toLocaleString('vi-VN')} thưởng`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}

      <Modal open={!!act} modalHeading={act ? (act.kind === 'pay' ? 'Chi' : act.kind === 'approve' ? 'Duyệt chi' : 'Từ chối') + ` ${act.rows.length} thưởng?` : ''}
        danger={act?.kind === 'reject'}
        primaryButtonText={act?.kind === 'pay' ? 'Chi ngay' : act?.kind === 'approve' ? 'Duyệt và chi' : 'Từ chối'} secondaryButtonText="Hủy"
        primaryButtonDisabled={run.isPending || (act?.kind === 'reject' && note.trim().length < 3)}
        onRequestClose={() => setAct(null)} onRequestSubmit={() => run.mutate()}>
        {act ? (
          <div className="stack">
            <p>Tổng <strong><Money v={total(act.rows)} unit /></strong> cho {new Set(act.rows.map((r) => r.gameUserId)).size} người chơi.
              {act.kind !== 'reject' ? ' Mỗi thưởng thành một lệnh cộng tiền có khóa riêng, kèm vòng cược (thưởng lên cấp theo hệ số sự kiện, hoàn trả theo hệ số hoàn trả) và một thư trong hộp thư người chơi. Tiền được cộng ngay, không chờ người duyệt.' : ''}</p>
            <TextArea id="vip-note" labelText={act.kind === 'reject' ? 'Lý do từ chối' : 'Ghi chú (tùy chọn)'} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            {act.kind !== 'reject' ? <TextArea id="vip-ack" labelText="Đã kiểm tra hoàn cược (chỉ khi thưởng bị giữ vì hoàn cược)" rows={2} value={ack} onChange={(e) => setAck(e.target.value)}
              helperText="Để trống: thưởng mà người chơi có hoàn cược có thể chạm tới sẽ được giữ lại. Ghi rõ đã kiểm tra gì (ít nhất 10 ký tự) để chi cả những thưởng đó." /> : null}
            <InlineError error={run.error} />
          </div>
        ) : null}
      </Modal>
      <Modal open={!!pick} passiveModal modalHeading={pick ? `${KIND[pick.kind]} · ${pick.nick || '#' + pick.gameUserId}` : ''} onRequestClose={() => setPick(null)}>
        {pick ? (
          <div className="stack">
            <dl className="facts">
              <dt>Kỳ / cấp</dt><dd>{pick.refLabel}</dd>
              <dt>Cấp VIP khi tính</dt><dd>{pick.levelName}</dd>
              {pick.kind === 'REBATE' ? <><dt>Tiền cược tính</dt><dd><Money v={pick.basis} /> · {(pick.rateBps / 100).toLocaleString('vi-VN')}%</dd></> : null}
              <dt>Số tiền</dt><dd><Money v={pick.amount} unit /></dd>
              <dt>Trạng thái</dt><dd><RewardStatus s={pick.status} /></dd>
              {pick.approvableAt && (pick.status === 'PROPOSED' || pick.status === 'SUBMITTED') ? <><dt>Chi được từ</dt><dd><When v={pick.approvableAt} /></dd></> : null}
              {pick.commandKey ? <><dt>Khóa lệnh</dt><dd className="num">{pick.commandKey}</dd></> : null}
              {pick.paidAt ? <><dt>Đã chi lúc</dt><dd><When v={pick.paidAt} /></dd></> : null}
              {pick.decisionNote ? <><dt>Ghi chú</dt><dd>{pick.decisionNote}</dd></> : null}
            </dl>
            <div className="row">
              {pick.status === 'PROPOSED' && can('vip.payout.submit') ? <Button size="sm" onClick={() => { run.reset(); setAct({ kind: 'pay', rows: [pick] }); setPick(null); }}>Chi ngay</Button> : null}
              {pick.status === 'SUBMITTED' && can('vip.payout.approve') ? <Button size="sm" onClick={() => { run.reset(); setAct({ kind: 'approve', rows: [pick] }); setPick(null); }}>Duyệt</Button> : null}
              {(pick.status === 'PROPOSED' || pick.status === 'SUBMITTED') && can('vip.payout.approve')
                ? <Button size="sm" kind="danger--ghost" onClick={() => { run.reset(); setAct({ kind: 'reject', rows: [pick] }); setPick(null); }}>Từ chối</Button> : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// ------------------------------------------------------------------ settings
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';
const num = (v: unknown, min: number, max: number) => Math.min(max, Math.max(min, Math.trunc(Number(v) || 0)));

// level rows: inputs with and without helper text line up at the top
const TOP = { verticalAlign: 'top' } as const;

export function VipConfigPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const edit = can('vip.config');
  const q = useQuery({ queryKey: ['vip-config'], queryFn: () => api.get<Config>('/api/admin/vip/config') });
  const [draft, setDraft] = useState<{ p: Program; expect: string | null } | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string; subtitle: string } | null>(null);
  const live = draft?.p ?? q.data?.program;
  const set = (patch: Partial<Program>) => live && setDraft({ p: { ...live, ...patch }, expect: draft?.expect ?? q.data?.updatedAt ?? null });
  const setLevel = (i: number, patch: Partial<Level>) => live && set({ cap: live.cap.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const save = useMutation({
    mutationFn: () => api.put<Config>('/api/admin/vip/config', { program: live, expect: draft?.expect ?? q.data?.updatedAt ?? null }),
    onSuccess: () => {
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['vip-config'] });
      setNotice({ kind: 'success', title: 'Đã lưu cấu hình VIP', subtitle: 'Cấp và điểm của mọi người chơi được tính lại theo cấu hình mới trong vài giây.' });
    },
    onError: (e) => {
      if (!isStale(e)) return;
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['vip-config'] });
      setNotice({ kind: 'warning', title: 'Cấu hình vừa được người khác lưu', subtitle: 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.' });
    },
  });
  // client-side mirror of the server rules, so the form says what is wrong before saving
  const problems: string[] = [];
  live?.cap.forEach((l, i) => {
    if (!l.ten.trim()) problems.push(`Cấp ${i + 1} chưa có tên`);
    if (i > 0 && l.diem <= live.cap[i - 1]!.diem) problems.push(`Điểm cấp ${i + 1} phải lớn hơn cấp ${i}`);
  });
  if (live && live.capChatVip > live.cap.length) problems.push('Cấp mở chat VIP không có');

  return (
    <div className="page">
      <PageHeader title="Cấu hình VIP"
        lead="Điểm VIP tính từ tiền cược thật đã chốt (trừ cược được hoàn), chỉ khi chương trình đang bật. Cấp chỉ lên, không tự xuống. Mọi thưởng vào danh sách chờ chi, không tự cộng tiền: quản trị viên bấm chi." />
      {q.isLoading || !live ? (q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <BlockSkeleton lines={10} />) : (
        <>
          <p className="muted">{q.data?.updatedAt ? <>Lưu lần cuối <When v={q.data.updatedAt} /></> : 'Chưa lưu lần nào: đang dùng mặc định (tắt, mọi tỷ lệ bằng 0).'}
            {live.bat && live.batLuc ? <> · tính điểm từ <When v={live.batLuc} /></> : null}</p>
          {!edit ? <InlineNotification kind="info" lowContrast hideCloseButton title="Chỉ xem" subtitle="Tài khoản của bạn không có quyền đổi cấu hình VIP." /> : null}
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          <section className="section">
            <h2>Chương trình</h2>
            <div className="stack">
              <Toggle id="vip-bat" labelText="Chương trình VIP" labelA="Tắt: không tính điểm, không tạo thưởng" labelB="Bật"
                toggled={live.bat} disabled={!edit} onToggle={(v) => set({ bat: v })} />
              <div className="row row--top">
                <NumberInput id="vip-vmd" label="Tiền cược cho 1 điểm VIP" helperText="Ví dụ 10.000: cược 1.000.000 được 100 điểm." min={1} max={1_000_000_000}
                  value={live.vinMoiDiem} disabled={!edit} onChange={(_e, { value }) => set({ vinMoiDiem: num(value, 1, 1_000_000_000) })} />
                <NumberInput id="vip-chat" label="Cấp mở chat CSKH VIP" helperText="Nút hỗ trợ trong game mở link chat VIP (trang Nội dung sảnh). 0 = không ai." min={0} max={20}
                  value={live.capChatVip} disabled={!edit} onChange={(_e, { value }) => set({ capChatVip: num(value, 0, 20) })} />
              </div>
              <div className="row row--top">
                <Select id="vip-ky" labelText="Kỳ hoàn trả" value={live.chuKyHoanTra} disabled={!edit} onChange={(e) => set({ chuKyHoanTra: e.target.value as Program['chuKyHoanTra'] })}>
                  <SelectItem value="tuan" text={`Theo tuần (thứ Hai đến Chủ nhật, ${timeZoneLabel()})`} />
                  <SelectItem value="ngay" text={`Theo ngày (${timeZoneLabel()})`} />
                </Select>
                <NumberInput id="vip-min" label="Hoàn trả tối thiểu" helperText="Thấp hơn mức này thì không tạo dòng hoàn trả." min={1} max={1_000_000_000}
                  value={live.hoanTraToiThieu} disabled={!edit} onChange={(_e, { value }) => set({ hoanTraToiThieu: num(value, 1, 1_000_000_000) })} />
              </div>
              <TextInput id="vip-skip" labelText="Game không tính điểm và hoàn trả" helperText="Tên game trong sổ giao dịch, cách nhau bằng dấu phẩy. Live Casino, Poker, Mega Poker, Bắn Cá và xu không bao giờ được tính."
                value={live.boQuaGame.join(', ')} disabled={!edit}
                onChange={(e) => set({ boQuaGame: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 200) })} />
              <NumberInput id="vip-cho" label="Thưởng chờ trước khi chi (giờ)" min={0} max={720}
                helperText="Thưởng lên cấp và hoàn trả chỉ chi được sau số giờ này, khi tiền cược tạo ra chúng đã qua thời hạn hoàn cược (lô đề hoàn vé chậm nhất khoảng 58 giờ)."
                value={live.choDuyetGio} disabled={!edit} onChange={(_e, { value }) => set({ choDuyetGio: num(value, 0, 720) })} />
            </div>
          </section>
          <section className="section">
            <h2>Các cấp</h2>
            <p className="muted">Hoàn trả tính trên tiền cược đã chốt của kỳ, theo cấp của người chơi lúc kỳ kết thúc. 100 bps = 1%, tối đa 500 bps. Thưởng lên cấp trả một lần cho mỗi cấp.</p>
            {live.cap.length ? (
              <div style={{ overflowX: 'auto' }}>
              <table className="plain section">
                <thead><tr><th>Cấp</th><th>Tên</th><th>Điểm cần</th><th>Thưởng lên cấp</th><th>Hoàn trả (bps)</th><th>Hoàn trả tối đa mỗi kỳ</th><th /></tr></thead>
                <tbody>
                  {live.cap.map((l, i) => (
                    <tr key={i}>
                      <td className="num" style={TOP} data-label="Cấp">{l.cap}</td>
                      <td style={TOP} data-label="Tên"><TextInput id={'vt-' + i} labelText={'Tên cấp ' + l.cap} hideLabel size="sm" value={l.ten} maxLength={40} disabled={!edit} onChange={(e) => setLevel(i, { ten: e.target.value })} /></td>
                      <td style={TOP} data-label="Điểm cần"><NumberInput id={'vd-' + i} label={'Điểm cần cấp ' + l.cap} hideLabel size="sm" min={0} value={l.diem} disabled={!edit} onChange={(_e, { value }) => setLevel(i, { diem: num(value, 0, 1e12) })} /></td>
                      <td style={TOP} data-label="Thưởng lên cấp"><NumberInput id={'vb-' + i} label={'Thưởng lên cấp cấp ' + l.cap} hideLabel size="sm" min={0} value={l.thuongLenCap} disabled={!edit} onChange={(_e, { value }) => setLevel(i, { thuongLenCap: num(value, 0, 1e10) })} /></td>
                      <td style={TOP} data-label="Hoàn trả (bps)"><NumberInput id={'vr-' + i} label={'Hoàn trả (bps) cấp ' + l.cap} hideLabel size="sm" min={0} max={500} value={l.hoanTraBps} disabled={!edit} onChange={(_e, { value }) => setLevel(i, { hoanTraBps: num(value, 0, 500) })} /></td>
                      <td style={TOP} data-label="Hoàn trả tối đa mỗi kỳ"><NumberInput id={'vm-' + i} label={'Hoàn trả tối đa cấp ' + l.cap} hideLabel size="sm" min={0} value={l.hoanTraToiDa} helperText={l.hoanTraToiDa ? undefined : 'Không giới hạn'} disabled={!edit} onChange={(_e, { value }) => setLevel(i, { hoanTraToiDa: num(value, 0, 1e10) })} /></td>
                      <td style={TOP}>{edit && i === live.cap.length - 1 ? <Button kind="ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Bỏ cấp cao nhất" onClick={() => set({ cap: live.cap.slice(0, -1), capChatVip: Math.min(live.capChatVip, live.cap.length - 1) })} /> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : <EmptyState title="Chưa có cấp nào" body="Thêm cấp đầu tiên để người chơi có thể lên VIP." />}
            {edit && live.cap.length < 20 ? (
              <Button kind="ghost" size="sm" renderIcon={Add} onClick={() => {
                const last = live.cap[live.cap.length - 1];
                set({ cap: [...live.cap, { cap: live.cap.length + 1, ten: 'VIP ' + (live.cap.length + 1), diem: last ? last.diem * 2 || 1 : 100, thuongLenCap: 0, hoanTraBps: 0, hoanTraToiDa: 0 }] });
              }}>Thêm cấp</Button>
            ) : null}
          </section>
          {problems.length ? <InlineNotification kind="error" lowContrast hideCloseButton title="Chưa lưu được" subtitle={problems.join('. ')} /> : null}
          {edit ? (
            <div className="row section">
              <Button disabled={!draft || problems.length > 0} onClick={() => { save.reset(); setOpen(true); }}>Lưu</Button>
              {draft ? <Button kind="ghost" onClick={() => setDraft(null)}>Bỏ thay đổi</Button> : null}
            </div>
          ) : null}
          <Modal open={open} modalHeading="Lưu cấu hình VIP?" primaryButtonText="Lưu" secondaryButtonText="Hủy"
            primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
            <div className="stack">
              <p>Chương trình: <strong>{live.bat ? 'bật' : 'tắt'}</strong>{live.bat && !q.data?.program.bat ? ' (chỉ tiền cược từ lúc bật mới được tính)' : ''}. {live.cap.length} cấp, {live.vinMoiDiem.toLocaleString('vi-VN')} tiền cược cho 1 điểm, hoàn trả {live.chuKyHoanTra === 'tuan' ? 'theo tuần' : 'theo ngày'}.</p>
              <p className="muted">Thưởng lên cấp: {live.cap.filter((l) => l.thuongLenCap > 0).map((l) => `${l.ten} ${l.thuongLenCap.toLocaleString('vi-VN')}`).join(', ') || 'không có'}. Hoàn trả: {live.cap.filter((l) => l.hoanTraBps > 0).map((l) => `${l.ten} ${(l.hoanTraBps / 100).toLocaleString('vi-VN')}%`).join(', ') || 'không có'}.</p>
              <InlineError error={save.error} />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ one player (UserDetail tab)
type PlayerVip = {
  enabled: boolean;
  levels: { level: number; name: string; points: number }[];
  player: { points: string; turnover: string; bonusPoints: string; level: number; levelName: string; levelLock: number | null; levelAt: string | null; pushed: boolean;
    next: { level: number; name: string; points: number; missing: string } | null };
  adjustments: { id: string; pointsDelta: string; levelLock: number | null; clearLock: boolean; reason: string; adminName: string; createdAt: string }[];
  rewards: (Reward & { refLabel: string })[];
};

export function VipPanel({ userId }: { userId: number }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['user-vip', userId], queryFn: () => api.get<PlayerVip>(`/api/admin/users/${userId}/vip`) });
  const [open, setOpen] = useState(false);
  const [points, setPoints] = useState('');
  const [lock, setLock] = useState<string>('keep');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${userId}/vip/adjust`, {
      ...(points.trim() ? { points: points.trim() } : {}),
      ...(lock === 'keep' ? {} : { levelLock: lock === 'clear' ? null : Number(lock) }),
      reason,
    }),
    onSuccess: () => { setOpen(false); setPoints(''); setLock('keep'); setReason(''); qc.invalidateQueries({ queryKey: ['user-vip', userId] }); qc.invalidateQueries({ queryKey: ['user', userId] }); },
  });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const { player: p, levels, rewards } = q.data;
  const valid = reason.trim().length >= 5 && (/^-?\d{1,12}$/.test(points.trim()) && Number(points) !== 0 || lock !== 'keep') && (!points.trim() || /^-?\d{1,12}$/.test(points.trim()));
  return (
    <div className="stack">
      {!q.data.enabled ? <InlineNotification kind="info" lowContrast hideCloseButton title="Chương trình VIP đang tắt" subtitle="Điểm không được tính thêm cho tới khi bật lại." /> : null}
      <div className="kpis">
        <div className="kpi"><span>Cấp</span><strong>{p.levelName}{p.levelLock != null ? ' (cố định)' : ''}</strong></div>
        <div className="kpi"><span>Điểm VIP</span><strong className="num">{Number(p.points).toLocaleString('vi-VN')}</strong></div>
        <div className="kpi"><span>Tiền cược đã tính</span><strong><Money v={p.turnover} /></strong></div>
        <div className="kpi"><span>Cấp tiếp theo</span><strong>{p.next ? `${p.next.name}, còn ${Number(p.next.missing).toLocaleString('vi-VN')} điểm` : 'Cao nhất'}</strong></div>
      </div>
      <p className="muted">Điểm do quản trị viên cộng / trừ: {Number(p.bonusPoints).toLocaleString('vi-VN')}{p.levelAt ? <> · đổi cấp lúc <When v={p.levelAt} /></> : null} · {p.pushed ? 'game đã nhận điểm mới nhất' : 'đang gửi điểm mới sang game'}</p>
      {can('vip.adjust') ? <div><Button size="sm" kind="tertiary" onClick={() => { m.reset(); setOpen(true); }}>Điều chỉnh VIP</Button></div> : null}
      <h3>Thưởng VIP</h3>
      {rewards.length ? (
        <ul className="list">
          {rewards.map((r) => (
            <li key={r.id}>{KIND[r.kind]} {r.refLabel}: <Money v={r.amount} unit /> <RewardStatus s={r.status} /> <span className="muted"><When v={r.createdAt} /></span></li>
          ))}
        </ul>
      ) : <EmptyState title="Chưa có thưởng VIP" />}
      <Modal open={open} modalHeading="Điều chỉnh VIP" primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={!valid || m.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <TextInput id="vip-adj-p" labelText="Cộng hoặc trừ điểm" helperText="Số nguyên, âm để trừ. Lên cấp nhờ điểm cộng vẫn tạo thưởng lên cấp chờ chi." value={points}
            onChange={(e) => setPoints(e.target.value.replace(/[^\d-]/g, ''))} />
          <Select id="vip-adj-l" labelText="Cấp" value={lock} onChange={(e) => setLock(e.target.value)}>
            <SelectItem value="keep" text="Giữ nguyên" />
            {p.levelLock != null ? <SelectItem value="clear" text="Bỏ cố định, theo điểm" /> : null}
            <SelectItem value="0" text="Cố định: chưa có cấp" />
            {levels.map((l) => <SelectItem key={l.level} value={String(l.level)} text={'Cố định: ' + l.name} />)}
          </Select>
          <TextArea id="vip-adj-r" labelText="Lý do" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} helperText="Ít nhất 5 ký tự." />
          <InlineError error={m.error} />
        </div>
      </Modal>
    </div>
  );
}
